import { Fragment } from "react";
import { Empty, Typography } from "antd";
import { CADENCE_COLORS, cadenceCellRGB } from "../etatsSinistresFunctions";

const { Text } = Typography;
const PRIMARY = "#242166";
const rgb = (a) => (a ? `rgb(${a[0]},${a[1]},${a[2]})` : "transparent");
const fmt = (v) => (v != null ? Number(v).toLocaleString("fr-FR") : "0");
const fmtTaux = (v) => `${Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 1 })}%`;
// Cellule « nb » : sinistres distincts + (nb de règlements) entre parenthèses.
const nbCell = (sin, reg) => `${fmt(sin)} (${fmt(reg)})`;

// Légende de lecture (âge de développement) — reprend l'esprit de la capture de référence.
const LEGENDE = [
    { c: CADENCE_COLORS.age0, t: "Même année" },
    { c: CADENCE_COLORS.age1, t: "+1 an" },
    { c: CADENCE_COLORS.age2, t: "+2 ans" },
    { c: CADENCE_COLORS.age3, t: "+3 ans" },
    { c: CADENCE_COLORS.age4, t: "+4 ans+" },
    { c: CADENCE_COLORS.reprise, t: "Reprise historique" },
];

/**
 * Triangle de liquidation : lignes = année de survenance, colonnes = année de règlement
 * (nb + montant par cellule), + colonne TOTAL, nb Déclarés et Taux (nb réglé / nb déclaré).
 * Le modèle est produit par buildCadenceModel().
 */
const CadenceTable = ({ model }) => {
    if (!model || !model.rows?.length || (model.totals?.totalNbSinistres === 0 && model.totals?.declares === 0)) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Text type="secondary">Aucune donnée de cadence pour ces critères.</Text>} />;
    }
    const { buckets, rows, totals } = model;

    const th = { border: "1px solid #e6e8f0", padding: "6px 8px", fontSize: 12, whiteSpace: "nowrap", textAlign: "center" };
    const thDark = { ...th, background: PRIMARY, color: "#fff", fontWeight: 600 };
    const tdBase = { border: "1px solid #eef0f4", padding: "5px 8px", fontSize: 12, textAlign: "right", whiteSpace: "nowrap" };

    return (
        <div>
            {/* Légende */}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14, marginBottom: 12 }}>
                <Text strong style={{ fontSize: 12 }}>Lecture :</Text>
                {LEGENDE.map((l) => (
                    <span key={l.t} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280" }}>
                        <span style={{ width: 16, height: 16, borderRadius: 4, background: rgb(l.c), border: "1px solid #d9d9d9" }} />
                        {l.t}
                    </span>
                ))}
            </div>

            <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 900 }}>
                    <thead>
                        <tr>
                            <th rowSpan={2} style={{ ...thDark, textAlign: "left" }}>Survenus en</th>
                            {buckets.map((b) => (
                                <th key={b.key} colSpan={2} style={thDark}>Payés en {b.label}</th>
                            ))}
                            <th colSpan={2} style={{ ...thDark, background: "#3a2f6e" }}>TOTAL</th>
                            <th rowSpan={2} style={thDark}>Déc.</th>
                            <th rowSpan={2} style={thDark}>Taux</th>
                        </tr>
                        <tr>
                            {buckets.map((b) => (
                                <Fragment key={b.key}>
                                    <th style={{ ...th, background: "#f0f1f7", fontWeight: 600, color: "#4b5563" }}>sin. (règl.)</th>
                                    <th style={{ ...th, background: "#f0f1f7", fontWeight: 600, color: "#4b5563" }}>mt (FCFA)</th>
                                </Fragment>
                            ))}
                            <th style={{ ...th, background: "#ece9f5", fontWeight: 600, color: "#4b5563" }}>sin. (règl.)</th>
                            <th style={{ ...th, background: "#ece9f5", fontWeight: 600, color: "#4b5563" }}>mt (FCFA)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.bucket.key}>
                                <td style={{ ...tdBase, textAlign: "left", fontWeight: 700, color: PRIMARY, background: "#fafbff" }}>{r.bucket.label}</td>
                                {buckets.map((cb) => {
                                    const c = r.cells[cb.key];
                                    const color = cadenceCellRGB(r.bucket, cb);
                                    const has = c.nbSinistres || c.nbReglements || c.montant;
                                    const bg = rgb(color);
                                    return (
                                        <Fragment key={cb.key}>
                                            <td style={{ ...tdBase, background: bg, fontWeight: has ? 600 : 400, color: has ? "#1f2937" : "#c0c4cc" }}>{has ? nbCell(c.nbSinistres, c.nbReglements) : "—"}</td>
                                            <td style={{ ...tdBase, background: bg, color: has ? "#1f2937" : "#c0c4cc" }}>{has ? fmt(c.montant) : "—"}</td>
                                        </Fragment>
                                    );
                                })}
                                <td style={{ ...tdBase, fontWeight: 700, background: "#f7f6fc" }}>{nbCell(r.totalNbSinistres, r.totalNbReglements)}</td>
                                <td style={{ ...tdBase, fontWeight: 700, background: "#f7f6fc" }}>{fmt(r.totalMontant)}</td>
                                <td style={{ ...tdBase }}>{fmt(r.declares)}</td>
                                <td style={{ ...tdBase, fontWeight: 700, color: r.taux >= 40 ? "#d46b08" : PRIMARY }}>{fmtTaux(r.taux)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td style={{ ...tdBase, textAlign: "left", fontWeight: 700, background: PRIMARY, color: "#fff" }}>TOTAL</td>
                            {buckets.map((cb) => (
                                <Fragment key={cb.key}>
                                    <td style={{ ...tdBase, fontWeight: 700, background: "#eef0f7" }}>{nbCell(totals.cells[cb.key].nbSinistres, totals.cells[cb.key].nbReglements)}</td>
                                    <td style={{ ...tdBase, fontWeight: 700, background: "#eef0f7" }}>{fmt(totals.cells[cb.key].montant)}</td>
                                </Fragment>
                            ))}
                            <td style={{ ...tdBase, fontWeight: 700, background: "#e6e2f3" }}>{nbCell(totals.totalNbSinistres, totals.totalNbReglements)}</td>
                            <td style={{ ...tdBase, fontWeight: 700, background: "#e6e2f3" }}>{fmt(totals.totalMontant)}</td>
                            <td style={{ ...tdBase, fontWeight: 700, background: "#eef0f7" }}>{fmt(totals.declares)}</td>
                            <td style={{ ...tdBase, fontWeight: 700, background: "#eef0f7", color: PRIMARY }}>{fmtTaux(totals.taux)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default CadenceTable;
